import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function AdminEmailSettings() {
  const { user } = useAuth();
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      setLoading(false);
      return;
    }
    loadEmails();
  }, [user]);

  const initializeSettings = async () => {
    try {
      // Create initial settings document if it doesn't exist
      await setDoc(doc(db, 'settings', 'contact'), {
        emails: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      return [];
    } catch (error) {
      console.error('Error initializing settings:', error);
      throw error;
    }
  };

  const loadEmails = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'contact'));
      if (!settingsDoc.exists()) {
        // Initialize settings if they don't exist
        const initialEmails = await initializeSettings();
        setEmails(initialEmails);
      } else {
        setEmails(settingsDoc.data().emails || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Failed to load contact emails');
      setLoading(false);
    }
  };

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || user.role !== 'admin') {
      toast.error('Only admins can manage contact emails');
      return;
    }
    
    if (!newEmail.trim()) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const updatedEmails = [...emails, newEmail.trim()];
      await setDoc(doc(db, 'settings', 'contact'), {
        emails: updatedEmails,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setEmails(updatedEmails);
      setNewEmail('');
      toast.success('Email added successfully');
    } catch (error) {
      console.error('Error adding email:', error);
      toast.error('Failed to add email');
    }
  };

  const handleRemoveEmail = async (emailToRemove: string) => {
    if (!user || user.role !== 'admin') {
      toast.error('Only admins can manage contact emails');
      return;
    }

    try {
      const updatedEmails = emails.filter(email => email !== emailToRemove);
      await setDoc(doc(db, 'settings', 'contact'), {
        emails: updatedEmails,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setEmails(updatedEmails);
      toast.success('Email removed successfully');
    } catch (error) {
      console.error('Error removing email:', error);
      toast.error('Failed to remove email');
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="text-gray-600 mt-2">Only administrators can manage contact emails.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-4">Contact Email Settings</h2>
        <p className="text-gray-600 mb-4">
          Add email addresses that will receive contact form submissions.
        </p>
      </div>

      <form onSubmit={handleAddEmail} className="flex gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Add new email address"
          className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </form>

      <div className="space-y-2">
        {emails.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No contact emails configured</p>
        ) : (
          emails.map((email) => (
            <div
              key={email}
              className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
            >
              <span>{email}</span>
              <button
                onClick={() => handleRemoveEmail(email)}
                className="text-red-600 hover:text-red-700"
                title="Remove email"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 